import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import {
  Star, MapPin, Sparkles, Tent, Heart, Instagram, Facebook,
  Download, Sun, Trees, Copy, Check, ArrowLeft, Mail, Globe,
} from "lucide-react";
import { SOCIAL } from "../lib/social";
import { withUtm } from "../lib/utm";

/**
 * /share/frowen-fields-reel — 9:16 promo for Frowen Fields camping (Pembrokeshire).
 *
 * Uses the existing site cover photo (/images/frowen-cover.jpg, supplied by
 * the owners). Bright, sunny palette to match the seaside vibe. Three
 * scenes built for one-tap distribution to Instagram, Facebook + Stories.
 *
 *   Scene 1 — THE PLACE: cover photo + "Pembrokeshire's wild west coast"
 *   Scene 2 — WHY HERE: 3 reasons + tagline ("camping how it should be")
 *   Scene 3 — OWNER SHOUT-OUT: warm "book direct with the owners" frame
 *   Scene 4 — THE LINK: QR + every social handle + "open the app in 1 tap"
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const LISTING_URL = withUtm(`${PUBLIC_URL}/campsites/cs_osm_1168515653`, {
  source: "frowen",
  medium: "reel",
  campaign: "frowen-fields-2026",
});
const APP_URL = withUtm(PUBLIC_URL, { source: "frowen", medium: "reel", campaign: "frowen-fields-app-install" });

const COVER_IMG = "/images/frowen-cover.jpg";

const CAPTION_LONG = `Welcome to Frowen Fields 🌾☀️

Tucked into Pembrokeshire's wild west coast, Frowen Fields is camping how it should be — slow mornings, salty walks, no algorithms shouting at you.

✓ Dog-friendly fields with proper space
✓ Coastal walks straight from the gate
✓ Direct-book — no booking-fee middleman

Find them on CampCompass UK — one tap, one app, no scrolling Google Maps at 11pm.

📸 ${SOCIAL?.instagram?.handle || "@camp.compassuk2026"}
👍 ${SOCIAL?.facebook?.handle || "@campcompassuk2026"}

camp-compass.uk · install in one tap from the home screen

#FrowenFields #Pembrokeshire #UKCamping #CampCompass #DirectBook`;

const CAPTION_SHORT = `Frowen Fields · Pembrokeshire 🌾☀️
Direct-book · dog-friendly · coastal walks from the gate.

camp-compass.uk · 1-tap install 🏕️`;

function Sticker({ children, color = "#ff4500", textColor = "#ffffff" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#ff4500]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden shadow-[0_4px_0_0_#0e0e0e]"
        style={{
          aspectRatio: "9 / 16",
          maxWidth: 380,
          background: bg,
        }}
        data-testid={testid}
      >
        {children}
      </section>
    </div>
  );
}

function CaptionBox({ label, text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border hairline bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{label}</div>
        <button
          onClick={() => {
            navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#ff4500] hover:text-[#0e0e0e]"
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-xs text-[#333] leading-relaxed font-sans">{text}</pre>
    </div>
  );
}

export default function FrowenFieldsReel() {
  const iHandle = SOCIAL?.instagram?.handle || "@camp.compassuk2026";
  const fHandle = SOCIAL?.facebook?.handle || "@campcompassuk2026";

  return (
    <div className="min-h-screen bg-[#fffbf2]" data-testid="frowen-fields-reel">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/launch-pack" className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#666] hover:text-[#0e0e0e] mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to print pack
        </Link>
        <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2">
          <Sun className="w-3 h-3" /> Frowen Fields · Pembrokeshire promo
        </div>
        <h1 className="font-display text-4xl tracking-tight mb-2">Frowen Fields · 3-scene reel · 9:16</h1>
        <p className="text-[#555] mb-6 max-w-2xl">
          Bright, sunny pitch using the supplied Frowen Fields cover photo.
          Built for one-tap share to Instagram + Facebook + Stories.
        </p>

        {/* Scenes */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* SCENE 1 — THE PLACE */}
          <Scene label="01 · The place" testid="frowen-scene-1" bg="#fbbf24">
            <div className="absolute inset-0">
              <img
                src={COVER_IMG}
                alt="Frowen Fields camping in Pembrokeshire"
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              {/* Sun-warm overlay so text always reads. */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" aria-hidden="true" />
            </div>
            <div className="relative p-5 pt-7">
              <Sticker color="#ffffff" textColor="#0e0e0e">
                <Tent className="w-3 h-3" /> Direct book
              </Sticker>
            </div>
            <div className="relative p-5 pb-7">
              <div className="font-display text-[34px] leading-[0.95] tracking-tight mb-3" style={{ textShadow: "0 2px 0 rgba(0,0,0,0.4)" }}>
                Frowen <br/>
                <span className="text-[#fbbf24]">Fields.</span>
              </div>
              <div className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-[0.2em] mb-1">
                <MapPin className="w-3 h-3" /> Pembrokeshire · Wales
              </div>
              <div className="text-sm leading-snug opacity-90 max-w-[260px]">
                Camping how it should be — slow mornings, salty walks, no app shouting at you.
              </div>
            </div>
          </Scene>

          {/* SCENE 2 — WHY HERE */}
          <Scene label="02 · Why here" testid="frowen-scene-2" bg="linear-gradient(135deg, #16a34a 0%, #84cc16 50%, #fbbf24 100%)">
            <div className="relative p-5 pt-7">
              <Sticker color="#0e0e0e">
                <Heart className="w-3 h-3 fill-current" /> Why campers love it
              </Sticker>
            </div>
            <div className="relative p-5 space-y-3">
              {[
                { Icon: Trees, title: "Coastal walks from the gate", body: "Pembrokeshire Coast Path on your doorstep." },
                { Icon: Heart, title: "Dog-friendly, properly", body: "Big fields, no nose-to-tail pitching." },
                { Icon: Sparkles, title: "Direct-book, no booking fee", body: "Talk to the owners — keep £'s in the field." },
              ].map(({ Icon, title, body }, i) => (
                <div key={i} className="bg-white/95 text-[#0e0e0e] p-3 shadow-[0_2px_0_0_#0e0e0e]">
                  <div className="flex items-start gap-2.5">
                    <div className="p-1.5 bg-[#16a34a] text-white flex-shrink-0">
                      <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display text-[13px] leading-tight">{title}</div>
                      <div className="text-[11px] text-[#555] leading-snug mt-0.5">{body}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="relative p-5 pb-7">
              <div className="inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-[0.2em] text-[#0e0e0e] bg-white/90 px-2 py-1">
                <Star className="w-3 h-3 fill-[#ff4500] text-[#ff4500]" /> Featured on CampCompass UK
              </div>
            </div>
          </Scene>

          {/* SCENE 3 — OWNER SHOUT-OUT · warm direct-book frame */}
          <Scene
            label="03 · Meet the owners"
            testid="frowen-scene-3"
            bg="linear-gradient(180deg, #fef3c7 0%, #fbbf24 55%, #f59e0b 100%)"
          >
            <div className="relative p-5 pt-7">
              <Sticker color="#0e0e0e">
                <Heart className="w-3 h-3 fill-current" /> Book direct
              </Sticker>
            </div>
            <div className="relative px-5 text-[#0e0e0e] flex-1 flex flex-col justify-center">
              <div className="font-display text-[28px] leading-[0.95] tracking-tight mb-3">
                Skip the middleman.<br />
                <span className="text-[#0e0e0e]">Talk to the owners.</span>
              </div>
              <div className="text-[13px] leading-snug mb-5 max-w-[270px]">
                Frowen Fields is a small family-run site — no call centre, no booking fee, no algorithm deciding your pitch. Just a quick chat and you're in.
              </div>
              <div className="bg-white/95 p-3 space-y-2 shadow-[0_2px_0_0_#0e0e0e]">
                <div className="flex items-center gap-2 text-[11px] mono">
                  <Globe className="w-3.5 h-3.5 text-[#16a34a]" />
                  <span className="font-bold">frowenfields.com</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] mono">
                  <Mail className="w-3.5 h-3.5 text-[#16a34a]" />
                  <span>email · direct from the site</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] mono">
                  <Tent className="w-3.5 h-3.5 text-[#16a34a]" />
                  <span>no platform commission</span>
                </div>
              </div>
            </div>
            <div className="relative p-5 pb-7">
              <div className="inline-flex items-center gap-1.5 text-[10px] mono uppercase tracking-[0.22em] text-[#0e0e0e] bg-white/90 px-2 py-1">
                <Sparkles className="w-3 h-3 text-[#ff4500]" /> Featured partner · CampCompass UK
              </div>
            </div>
          </Scene>

          {/* SCENE 4 — THE LINK · all socials + 1-tap app install */}
          <Scene label="04 · 1-tap install" testid="frowen-scene-4" bg="linear-gradient(180deg, #0e0e0e 0%, #1a1a1a 100%)">
            <div className="relative p-5 pt-7">
              <Sticker color="#ff4500">
                <Download className="w-3 h-3" /> 1-tap install
              </Sticker>
            </div>
            <div className="relative p-5 flex flex-col items-center text-center">
              <div className="font-display text-[26px] leading-[0.95] tracking-tight mb-3">
                Open the app in <span className="text-[#ff4500]">one tap.</span>
              </div>
              <div className="text-[12px] text-white/80 mb-4 leading-snug max-w-[240px]">
                Scan → "Add to home screen" → done. No App Store. No 200MB download.
              </div>
              {/* QR — scan opens the app with UTM tagged for attribution. */}
              <div className="bg-white p-3 border-2 border-[#ff4500] mb-4">
                <QRCodeSVG value={APP_URL} size={130} level="M" bgColor="#ffffff" fgColor="#0e0e0e" />
              </div>
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-[#ff4500] mb-3">
                camp-compass.uk
              </div>
              {/* Socials — both handles visible */}
              <div className="w-full space-y-1.5 text-[11px] mono">
                <div className="flex items-center gap-1.5 justify-center text-white/90">
                  <Instagram className="w-3.5 h-3.5 text-[#ff4500]" />
                  <span>{iHandle}</span>
                </div>
                <div className="flex items-center gap-1.5 justify-center text-white/90">
                  <Facebook className="w-3.5 h-3.5 text-[#ff4500]" />
                  <span>{fHandle}</span>
                </div>
              </div>
            </div>
            <div className="relative p-5 pb-7 text-center">
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-white/60">
                Frowen Fields · Pembrokeshire · find them in 1 tap
              </div>
            </div>
          </Scene>
        </div>

        {/* Captions */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <CaptionBox label="Long caption · Instagram + Facebook" text={CAPTION_LONG} />
          <CaptionBox label="Short caption · Stories + TikTok" text={CAPTION_SHORT} />
        </div>

        {/* Listing preview link */}
        <a
          href={LISTING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-5 py-3 bg-[#0e0e0e] text-white hover:bg-[#ff4500] transition-colors text-xs mono uppercase tracking-wider"
        >
          <Tent className="w-3.5 h-3.5" /> Preview the Frowen Fields listing
        </a>
      </div>
    </div>
  );
}
